import { useEffect } from 'react';
import type { PlanStepProps } from '../types';

export function BasicInfoStep({ data, onChange, onValidChange }: PlanStepProps) {
  useEffect(() => {
    const isValid = !!data.code.trim() && !!data.name.trim();
    onValidChange(isValid);
  }, [data.code, data.name, onValidChange]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Información Básica
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Define el código, nombre y descripción del plan
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.code}
              onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
              placeholder="PROFESSIONAL"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-brand-500 focus:border-transparent
                       placeholder:text-gray-400 dark:placeholder:text-gray-500 uppercase"
            />
            <p className="text-xs text-gray-500 mt-1">Identificador único (Ej: FREE, BASIC, PRO)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre del Plan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Profesional"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-brand-500 focus:border-transparent
                       placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Descripción
          </label>
          <textarea
            value={data.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Describe las características principales de este plan..."
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none
                     placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Período de Facturación
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onChange({ billingCycle: 'MONTHLY' })}
              className={`p-4 border-2 rounded-xl text-left transition-all ${
                data.billingCycle === 'MONTHLY'
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <span className={`font-semibold ${
                data.billingCycle === 'MONTHLY'
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-gray-800 dark:text-white'
              }`}>
                Mensual
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Facturación cada mes
              </p>
            </button>

            <button
              type="button"
              onClick={() => onChange({ billingCycle: 'YEARLY' })}
              className={`p-4 border-2 rounded-xl text-left transition-all ${
                data.billingCycle === 'YEARLY'
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <span className={`font-semibold ${
                data.billingCycle === 'YEARLY'
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-gray-800 dark:text-white'
              }`}>
                Anual
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Facturación cada año
              </p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4">
          <label
            className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all
                     hover:border-gray-300 dark:hover:border-gray-600"
            style={{
              borderColor: data.isActive ? 'var(--color-brand-500)' : undefined,
              backgroundColor: data.isActive ? 'var(--color-brand-50)' : undefined,
            }}
          >
            <input
              type="checkbox"
              checked={data.isActive}
              onChange={(e) => onChange({ isActive: e.target.checked })}
              className="w-5 h-5 text-brand-500 rounded border-gray-300 focus:ring-brand-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-800 dark:text-white">
                Plan Activo
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                Permite nuevas suscripciones a este plan
              </p>
            </div>
          </label>

          <label
            className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all
                     hover:border-gray-300 dark:hover:border-gray-600"
            style={{
              borderColor: data.isPublic ? 'var(--color-brand-500)' : undefined,
              backgroundColor: data.isPublic ? 'var(--color-brand-50)' : undefined,
            }}
          >
            <input
              type="checkbox"
              checked={data.isPublic}
              onChange={(e) => onChange({ isPublic: e.target.checked })}
              className="w-5 h-5 text-brand-500 rounded border-gray-300 focus:ring-brand-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-800 dark:text-white">
                Plan Público
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                Visible en la página de precios
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}
